// What priced a line, in one phrase a reader can take in at a glance.
//
// The interpretation writes a `basis` sentence for every line, and it is a
// good sentence: it says how many facts matched, which arm priced each one and
// how many the selector refused. It is also three clauses long, it is different
// on every row, and a column wide enough to hold it pushes the charge itself
// off the side of the screen. A reader scanning a month for the row that is
// wrong needs to sort rows, not read paragraphs.
//
// So the column carries a READING built from the structured fields the report
// already sends — the count, the unit, the price and the arm that supplied it —
// and the sentence stays one disclosure away, unchanged. Nothing is lost and
// the table fits.
//
// THE READING NAMES ONE THING: what supplied the figure. What the line could
// not price is a second claim, so it comes back as a second field the caller
// draws in its own tone. An `excluded` count is a rule working as written and
// is NOT a warning; every other count is a fact somebody has to go and fix.

import { UnpricedReason, type DealStatementUnpriced, type MonthCloseLine } from "@crm/shared";
import {
  figureSourcePriceWord,
  meterUnitPhrase,
  unpricedReasonShort,
} from "../../format/labels.js";
import { formatCount, formatQuantity, formatUnitPrice } from "../../format/money.js";
import { assertNever } from "../../lib/assertNever.js";

/** What a line could not price, and whether that is somebody's job to fix. */
export interface UnpricedClause {
  readonly text: string;
  /** True where a count is a shortfall. A refused fact is not one. */
  readonly warn: boolean;
}

export interface PricedByReading {
  /** What supplied this line's figure, in one phrase. */
  readonly price: string;
  /** What it could not price, or null where it priced everything. */
  readonly unpriced: UnpricedClause | null;
}

export function pricedByReading(line: MonthCloseLine): PricedByReading {
  return { price: priceReading(line), unpriced: unpricedClause(line.unpriced) };
}

/**
 * Whether this line's quantity is a COUNT a reader multiplies by a price.
 *
 * A flat charge carries a quantity of one period, and a charge whose figure is
 * money carries the money's own unit. Neither is a count: "1 period × the
 * contract" states nothing the words "Contracted amount" do not state better.
 */
function counted(line: MonthCloseLine): boolean {
  if (line.quantity === null || line.unit === null) return false;
  return line.unit !== "period" && line.unit !== "currency_minor";
}

function priceReading(line: MonthCloseLine): string {
  // A document that went out is the answer to "what priced this", and it
  // outranks every other arm: the figure on the row is the figure on the
  // paper, whatever the facts say today.
  if (line.invoice_number !== null) return `On invoice ${line.invoice_number}`;

  if (counted(line) && line.quantity !== null && line.unit !== null) {
    const units = `${formatQuantity(line.quantity)} ${meterUnitPhrase(line.unit, line.quantity)}`;
    const price =
      line.rate_minor === null
        ? figureSourcePriceWord(line.figure_source)
        : formatUnitPrice(line.rate_minor, line.currency, { omitCurrencyCode: true });
    return `${units} × ${price}`;
  }

  // A withheld figure is not a late one. The reason it is withheld already
  // rides under the amount, so this points at it rather than restating it.
  if (line.derived_issue !== null) return "Nothing priced it — see the amount";
  if (line.gross_minor === null) return "Waiting for a figure";

  switch (line.figure_source) {
    case "contract":
      return "Contracted amount";
    case "deal_fact":
      return "From a recorded fact";
    case "provider_cost":
      return "From the provider's figure";
    case "rate_card":
      return "Priced by our rate card";
    case "none":
      return "Waiting for a figure";
    default:
      return assertNever(line.figure_source, "figure source");
  }
}

/**
 * The counts a line could not price, as one clause.
 *
 * The reasons come from the CONTRACT's own list, so a reason added to it
 * appears here rather than being dropped by a renderer that never heard of it.
 */
function unpricedClause(unpriced: DealStatementUnpriced): UnpricedClause | null {
  const parts: string[] = [];
  let warn = false;
  for (const reason of UnpricedReason.options) {
    const count = unpriced[reason];
    if (count === 0) continue;
    parts.push(`${formatCount(count)} ${unpricedReasonShort(reason)}`);
    if (reason !== "excluded") warn = true;
  }
  return parts.length === 0 ? null : { text: parts.join(" · "), warn };
}
